// Pure, dependency-free helpers for the "My Tournaments" grid's folders / pins /
// custom-order layer (js/app.js renderTournamentsPage). Sits on top of
// js/utils/tournament-sort.js — it reuses that module's ordering primitives so
// the two share one definition of "newest/oldest/closure". No DOM, no state.js,
// no firebase, so it is trivially unit-testable (tests/tournament-organize.test.js).
//
// Organization data shape (state.tournamentOrg, cloud-synced):
//   {
//     folders: [{ id, name, createdAt, pinned, order }],
//     items:   { [tournamentId]: { folderId: string|null, pinned: bool, order: number|null } }
//   }

import {
    SORT_ORDERS as BASE_SORT_ORDERS,
    sortMyTournaments,
    getClosureTimestamp
} from './tournament-sort.js';

// The My Tournaments "Order" options, extended with CUSTOM (manual drag order).
export const SORT_ORDERS = { ...BASE_SORT_ORDERS, CUSTOM: 'custom' };

// The tournament id for a grid entry. Entries come as a live tournament spread
// with isActive:true (id at top level) or a history entry ({id, name, data}).
export function entryId(entry) {
    if (!entry) return '';
    const raw = entry.id !== undefined && entry.id !== null
        ? entry.id
        : (entry.data && entry.data.id);
    return raw === undefined || raw === null ? '' : String(raw);
}

// The per-tournament organization record, always fully shaped.
export function getItem(org, id) {
    const items = (org && org.items) || {};
    const rec = items[id];
    return {
        folderId: (rec && rec.folderId) || null,
        pinned: !!(rec && rec.pinned),
        order: rec && typeof rec.order === 'number' ? rec.order : null
    };
}

// A folder's manual drag position, or null when it has never been dragged.
export function folderOrder(folder) {
    return folder && typeof folder.order === 'number' ? folder.order : null;
}

// Orders a list of entries by the given order. CUSTOM sorts by each item's
// stored `order` (ascending; entries without one sort last, tie-broken by most
// recently closed/created). Any non-custom order defers to tournament-sort.js's
// sortMyTournaments. Never forces the active tournament first — callers pull the
// active entry out before ordering (see organizeTournaments).
function orderEntries(list, org, order) {
    const arr = Array.isArray(list) ? list.slice() : [];
    if (order === SORT_ORDERS.CUSTOM) {
        arr.sort((a, b) => {
            const oa = getItem(org, entryId(a)).order;
            const ob = getItem(org, entryId(b)).order;
            const na = oa === null;
            const nb = ob === null;
            if (na && nb) return getClosureTimestamp(b) - getClosureTimestamp(a);
            if (na) return 1;
            if (nb) return -1;
            return oa - ob;
        });
        return arr;
    }
    return sortMyTournaments(arr, order);
}

// Splits entries into pinned-first then the rest, each ordered by `order`.
function pinnedFirst(list, org, order) {
    const pinned = [];
    const rest = [];
    list.forEach(e => (getItem(org, entryId(e)).pinned ? pinned : rest).push(e));
    return orderEntries(pinned, org, order).concat(orderEntries(rest, org, order));
}

// Builds the render view-model for the My Tournaments grid.
//
//   list  — the already filter/search-narrowed grid entries (each may carry
//           isActive:true for the live tournament).
//   org   — state.tournamentOrg.
//   opts  — { order, filterActive }.
//
// filterActive === true  → a flat, pin-agnostic, folder-agnostic list ordered by
//   `order`, so a search/format/type/show filter surfaces every match (including
//   ones that live inside a collapsed folder). This is the "filter overrides
//   pins" behavior.
//
// filterActive === false → { folders, topLevel }:
//   folders  = [{ folder, tournaments }] — pinned folders first, then the rest by
//              creation order; each folder's tournaments pinned-first then ordered.
//   topLevel = the active tournament first (never nested while live), then pinned
//              top-level tournaments, then the rest — all by `order`.
export function organizeTournaments(list, org, opts = {}) {
    const order = opts.order || SORT_ORDERS.CLOSURE;
    const entries = Array.isArray(list) ? list : [];
    const safeOrg = { folders: (org && org.folders) || [], items: (org && org.items) || {} };

    if (opts.filterActive) {
        return { filterActive: true, flat: orderEntries(entries, safeOrg, order), folders: [], topLevel: [] };
    }

    const folderById = new Map(safeOrg.folders.map(f => [String(f.id), f]));

    let activeEntry = null;
    const byFolder = new Map(); // folderId -> entries[]
    const topLevelPool = [];

    entries.forEach(e => {
        const item = getItem(safeOrg, entryId(e));
        const fid = item.folderId && folderById.has(String(item.folderId)) ? String(item.folderId) : null;
        // The live tournament is shown first and un-nested ONLY while it isn't
        // filed in a folder — otherwise the user could never actually move their
        // open tournament into a folder (it would always jump back to the top).
        if (e && e.isActive && !activeEntry && !fid) {
            activeEntry = e;
            return;
        }
        if (fid) {
            if (!byFolder.has(fid)) byFolder.set(fid, []);
            byFolder.get(fid).push(e);
        } else {
            topLevelPool.push(e);
        }
    });

    // Folders: pinned first, then the rest by their dragged order (folders never
    // dragged have none and follow, by ascending createdAt — so a newly created
    // folder lands at the end until it is moved).
    const sortedFolders = safeOrg.folders.slice().sort((a, b) => {
        const pa = a.pinned ? 0 : 1;
        const pb = b.pinned ? 0 : 1;
        if (pa !== pb) return pa - pb;
        const oa = folderOrder(a);
        const ob = folderOrder(b);
        if (oa !== ob) {
            if (oa === null) return 1;
            if (ob === null) return -1;
            return oa - ob;
        }
        return (a.createdAt || 0) - (b.createdAt || 0);
    });

    const folders = sortedFolders.map(f => ({
        folder: f,
        tournaments: pinnedFirst(byFolder.get(String(f.id)) || [], safeOrg, order)
    }));

    const topLevel = (activeEntry ? [activeEntry] : []).concat(pinnedFirst(topLevelPool, safeOrg, order));

    return { filterActive: false, flat: [], folders, topLevel };
}

// Recomputes the custom drag order after a card is dropped onto another card.
//   org        — state.tournamentOrg.
//   orderedIds — the tournament ids currently displayed in the drag scope, in
//                display order (the exact list the user sees and is rearranging).
//   movedId    — the dragged tournament's id.
//   targetId   — the tournament it was dropped onto.
//   placeAfter — when true, movedId lands AFTER the target (drop on the target's
//                trailing half); otherwise before it. This is what lets a card be
//                dropped "between" two cards.
// Returns a NEW items map (org.items is not mutated): every id in the scope gets
// a fresh sequential `order`; ids outside the scope keep their existing record.
export function reorderCustom(org, orderedIds, movedId, targetId, placeAfter = false) {
    const items = (org && org.items) || {};
    const ids = (Array.isArray(orderedIds) ? orderedIds : []).map(String);
    const moved = String(movedId);
    const target = String(targetId);

    const without = ids.filter(id => id !== moved);
    let insertAt = without.indexOf(target);
    if (insertAt === -1) insertAt = without.length; // target gone → append
    else if (placeAfter) insertAt += 1;             // drop on target's trailing half
    without.splice(insertAt, 0, moved);

    const next = { ...items };
    without.forEach((id, i) => {
        const prev = items[id] || {};
        next[id] = { folderId: prev.folderId || null, pinned: !!prev.pinned, order: i };
    });
    return next;
}

// Recomputes the folder band order after a folder is dragged onto another one —
// the folder-level twin of reorderCustom.
//   org        — state.tournamentOrg.
//   orderedIds — the folder ids currently displayed, in display order.
//   movedId    — the dragged folder's id.
//   targetId   — the folder it was dropped onto.
//   placeAfter — when true, movedId lands BELOW the target, otherwise above it.
// Returns a NEW folders array (org.folders is not mutated): every folder in the
// displayed list gets a fresh sequential `order`; any folder outside it keeps
// its existing record.
export function reorderFolders(org, orderedIds, movedId, targetId, placeAfter = false) {
    const folders = (org && org.folders) || [];
    const ids = (Array.isArray(orderedIds) ? orderedIds : []).map(String);
    const moved = String(movedId);
    const target = String(targetId);

    const without = ids.filter(id => id !== moved);
    let insertAt = without.indexOf(target);
    if (insertAt === -1) insertAt = without.length; // target gone → append
    else if (placeAfter) insertAt += 1;             // dropped on the target's lower half
    without.splice(insertAt, 0, moved);

    const rank = new Map(without.map((id, i) => [id, i]));
    return folders.map(f => {
        const r = rank.get(String(f.id));
        return r === undefined ? f : { ...f, order: r };
    });
}
